/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { getOrientation } from 'get-orientation/browser';
import humanize from 'humanize-plus';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Img from 'react-image';
import { useSelector } from 'react-redux';

import { CircularProgress } from '@mui/material';

import { MAX_PHOTO_FILE_SIZE_IN_BYTES, ORIENTATION_TO_ANGLE } from '../../constants/settings';
import { Namespaces } from '../../i18n';
import { ConvertibleCanvas } from '../../miscellaneous/ConvertibleCanvas';
import { OrganizationApiService } from '../../services/OrganizationApiService';
import { ProjectApiService } from '../../services/ProjectApiService';
import { fetchEntityAsBase64 } from '../../services/ThumbnailApiService';
import { UserApiService } from '../../services/UserApiService';
import { RootState } from '../../store';
import { getRotatedImage, readFile } from '../../utils/canvasUtils';
import { AlignCenter } from '../AlignCenter/AlignCenter';
import { EDIT_PHOTO_ACCEPTABLE_FILES } from '../EditPhoto/EditPhoto';
import { EditPhotoModal } from '../EditPhotoModal/EditPhotoModal';
import { useEventCallback } from '../hooks/useEventCallback';
import { useEntityThumbnailStyles } from './EntityThumbnail.styles';
import { EditThumbnailDataType, EntityProps, ImageUploadProps } from './EntityThumbnail.types';

type EntityThumbnailProps = ImageUploadProps &
  EntityProps & {
    size?: number;
    className?: string;
    imageClassName?: string;
    loaderSize?: number;
    version?: string | number;
    mergeVersion?: boolean;
  };

export function EntityThumbnail(props: EntityThumbnailProps) {
  const {
    size = 24,
    loaderSize = size,
    className,
    imageClassName,
    allowUploadImage,
    onImageUpload,
    version,
    mergeVersion,
  } = props;
  const classes = useEntityThumbnailStyles();
  const { t } = useTranslation(Namespaces.Common);

  const [imageSrc, setImageSrc] = useState<string>();

  const thumbnailsVersion = useSelector((state: RootState) => state.resources.thumbnailsVersion);

  const totalVersion = useMemo(() => {
    if (mergeVersion) {
      return [version, thumbnailsVersion].join('_');
    }
    return version || thumbnailsVersion;
  }, [mergeVersion, thumbnailsVersion, version]);

  const [editThumbnailData, setEditThumbnailData] = useState<EditThumbnailDataType>({
    editImageSrc: '',
    isModalOpen: false,
  });

  const handleEditThumbnailData = (data: EditThumbnailDataType) => {
    setEditThumbnailData({
      editImageSrc: data.editImageSrc,
      isModalOpen: data.isModalOpen,
    });
  };

  useEffect(() => {
    switch (props.type) {
      case 'organization':
        void fetchEntityAsBase64(
          OrganizationApiService.getOrganizationThumbnailUrl({
            organizationId: props.organizationId,
            v: totalVersion,
          }),
        ).then(res => setImageSrc(res));
        break;
      case 'project':
        void fetchEntityAsBase64(
          ProjectApiService.getProjectThumbnailUrl({
            organizationId: props.organizationId,
            projectId: props.projectId,
            v: totalVersion,
          }),
        ).then(res => setImageSrc(res));
        break;
      case 'user':
        void fetchEntityAsBase64(UserApiService.getUserThumbnailUrl({ v: totalVersion })).then(res => setImageSrc(res));
        break;
      case 'custom_src':
        setImageSrc(props.src);
        break;
      default:
        setImageSrc(undefined);
    }
  }, [props.organizationId, props.projectId, props.type, totalVersion, props.src]);

  const onFileInputChange = useEventCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      e.target.files = null;
      e.target.value = '';

      if (file.size > MAX_PHOTO_FILE_SIZE_IN_BYTES) {
        alert(
          t(`warnings.exceeded_file_size_with_max`, {
            maxSize: humanize.fileSize(MAX_PHOTO_FILE_SIZE_IN_BYTES),
          }),
        );
        return;
      }

      let imageDataUrl = await readFile(file);
      const orientation = await getOrientation(file);
      const rotation = ORIENTATION_TO_ANGLE[orientation as number] ?? ORIENTATION_TO_ANGLE.__fallback;

      if (rotation) {
        imageDataUrl = await (await getRotatedImage(imageDataUrl, rotation)).toBase64();
      }

      handleEditThumbnailData({
        editImageSrc: imageDataUrl,
        isModalOpen: true,
      });
    }
  });

  const onCancel = () => {
    handleEditThumbnailData({
      editImageSrc: '',
      isModalOpen: false,
    });
  };

  const onSave = async (canvas: ConvertibleCanvas) => {
    onImageUpload!(await canvas.toBlob());
    onCancel();
  };

  // https://github.com/mbrevda/react-image/issues/356
  if (allowUploadImage) {
    return (
      <>
        <label
          className={cx(classes.root, className, classes.editable)}
          htmlFor="change_organization_thumb"
          style={{
            width: `${size}px`,
            height: `${size}px`,
          }}
          data-cy="entity-thumbnail-file-upload"
        >
          <Img
            loader={
              <AlignCenter>
                <CircularProgress color="primary" size={loaderSize} />
              </AlignCenter>
            }
            className={cx(classes.image, classes.editable, imageClassName)}
            src={imageSrc}
          />
        </label>
        <input
          type="file"
          onChange={onFileInputChange}
          id="change_organization_thumb"
          className={classes.fileInput}
          accept={EDIT_PHOTO_ACCEPTABLE_FILES}
        />
        {editThumbnailData.isModalOpen && (
          <EditPhotoModal
            onSave={onSave}
            onCancel={onCancel}
            open={editThumbnailData.isModalOpen}
            imageSrc={editThumbnailData.editImageSrc}
            size="big"
          />
        )}
      </>
    );
  }

  return (
    <div
      className={cx(classes.root, className)}
      style={{
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      <Img
        loader={
          <AlignCenter>
            <CircularProgress color="primary" size={loaderSize} />
          </AlignCenter>
        }
        src={imageSrc}
        className={cx(classes.image, classes.editable, imageClassName)}
      />
    </div>
  );
}
